/// <amd-module name="Repos/PullRequestList/Util" />
import { GitPullRequest, IdentityRefWithVote, PullRequestAsyncStatus, PullRequestStatus } from "Repos/Common/Contracts/Git";
import { IReposRoutingService } from "Repos/Common/ReposRoutingService";
import { branchToVersionDescriptor } from "Repos/Common/Util/Version";
import { ObservableValue } from "VSS/Core/Observable";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { encodeUri } from "VSS/Platform/Util/Linking";
import { IBasicIdentityRef, IPersonaProvider } from "VSSUI/Persona";
import { IPullRequest, IPullRequestReviewer, PullRequestVote } from "./Contracts";

export function convertPullRequest(
    pr: GitPullRequest,
    reposRoutingService: IReposRoutingService,
    currentUserId: string,
    personaProvider: IPersonaProvider
): IPullRequest {
    const targetBranchName = pr.targetRefName.substring("refs/heads/".length);
    const bypassedPolicies = pr.status === PullRequestStatus.Completed && !!pr.completionOptions && pr.completionOptions.bypassPolicy;
    return {
        autoCompleteSetBy: pr.autoCompleteSetBy && getBasicIdentityRef(pr.autoCompleteSetBy),
        bypassedPolicies,
        bypassedPoliciesReason: bypassedPolicies ? pr.completionOptions.bypassReason : undefined,
        closedDate: pr.closedDate,
        codeReviewId: pr.codeReviewId,
        codeReviewArtifactUri: encodeUri({
            tool: "CodeReview",
            type: pr.supportsIterations ? "ReviewId" : "CodeReviewId",
            id: pr.supportsIterations ? `${pr.repository.project.id}/${pr.codeReviewId}` : `${pr.repository.project.id}/${pr.pullRequestId}`
        }),
        commentsCount: new ObservableValue<number>(0),
        conflicts: pr.mergeStatus === PullRequestAsyncStatus.Conflicts,
        createdBy: getBasicIdentityRef(pr.createdBy),
        createdDate: pr.creationDate,
        currentReviewer: getReviewer(pr.reviewers, currentUserId),
        date: new ObservableValue<Date>(pr.creationDate),
        draft: pr.isDraft,
        id: pr.pullRequestId,
        labels: pr.labels,
        linkProps: { href: reposRoutingService.getPullRequestUrl(pr.repository.name, pr.repository.project.name, pr.pullRequestId) },
        personaProvider: personaProvider,
        pullRequestArtifactUri: encodeUri({
            tool: "Git",
            type: "PullRequestId",
            id: `${pr.repository.project.id}/${pr.repository.id}/${pr.pullRequestId}`
        }),
        repository: {
            id: pr.repository.id,
            href: reposRoutingService.getFilesUrl(pr.repository.name, pr.repository.project.name),
            name: pr.repository.name,
            projectId: pr.repository.project.id
        },
        reviewers: pr.reviewers.map(convertReviewer),
        status: pr.status,
        supportsIterations: pr.supportsIterations,
        targetRef: {
            href: reposRoutingService.getFilesUrl(
                pr.repository.name,
                pr.repository.project.name,
                undefined,
                branchToVersionDescriptor(targetBranchName)
            ),
            name: targetBranchName
        },
        title: pr.title,
        unvisited: new ObservableValue(false),
        updates: new ObservableValue("")
    };
}

function getReviewer(reviewers: IdentityRefWithVote[], userId: string): IPullRequestReviewer | undefined {
    const reviewer = reviewers.find(r => r.id === userId);
    return reviewer ? convertReviewer(reviewer) : undefined;
}

export function convertReviewer(reviewer: IdentityRefWithVote): IPullRequestReviewer {
    const identity = getBasicIdentityRef(reviewer);
    return {
        hasDeclined: reviewer.hasDeclined,
        identity: identity,
        isFlagged: reviewer.isFlagged,
        isGroup: reviewer.isContainer,
        required: reviewer.isRequired,
        vote: voteToStatus(reviewer.vote)
    };
}

function getBasicIdentityRef(identityRef: IdentityRef): IBasicIdentityRef {
    const { _links, imageUrl } = identityRef;
    const personaIdentity: IBasicIdentityRef = { id: identityRef.id, displayName: identityRef.displayName };

    if (_links && _links.avatar && _links.avatar.href) {
        personaIdentity.imageUrl = _links.avatar.href;
    } else if (imageUrl) {
        personaIdentity.imageUrl = imageUrl;
    }

    return personaIdentity;
}

function voteToStatus(vote: number): PullRequestVote {
    switch (vote) {
        case 10:
            return PullRequestVote.approved;
        case 5:
            return PullRequestVote.approvedWithComment;
        case 0:
            return PullRequestVote.awaitingResponse;
        case -5:
            return PullRequestVote.notReady;
        case -10:
        default:
            return PullRequestVote.rejected;
    }
}
